import { useEffect } from 'react'
import { createClient } from '@connectrpc/connect'
import { useQuery } from '@tanstack/react-query'

import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import {
  EventService,
  type SubscribeRequest,
} from '@jotta/grpc-connect-openapi/esm/no/jotta/openapi/event/v1/event.v1_pb'
import type { ContentRef } from '@jotta/grpc-connect-openapi/ref'
import type { PartialMessage } from '@jotta/types/compat'

import { authTransport } from './transport'

const eventClient = createClient(EventService, authTransport)

type Ref = ContentRef['ref']
type SubscriptionType = {
  listeners: Set<(contentRef: Ref) => void>
  queryKey: string
  request: PartialMessage<SubscribeRequest>
}

const fileSubscription: SubscriptionType = {
  listeners: new Set(),
  queryKey: 'file_event_subscription',
  request: {
    filesSubscription: {},
  },
}

const photoSubscription: SubscriptionType = {
  listeners: new Set(),
  queryKey: 'photo_event_subscription',
  request: {
    photoSubscription: {},
  },
}

const customerSubscription: SubscriptionType = {
  listeners: new Set(),
  queryKey: 'customer_event_subscription',
  request: {
    customerSubscription: {},
  },
}

function useSubscription(
  subscriptionType: SubscriptionType,
  listener: (ref: Ref) => void,
) {
  const { listeners: listenerSet, request, queryKey } = subscriptionType
  useEffect(() => {
    // make a local copy of the listener to protect against an edge case where the useFileSubscription hook
    // is used in multiple places with the same listener reference. Example: useFileSubscription(listenerA) + useFileSubscription(listenerA)
    // in this case, we want to maintain multiple copies in the global set to avoid stopping all subscriptions if only one
    // hook is unmounted
    const listenerCopy = (ref: Ref) => listener(ref)
    listenerSet.add(listenerCopy)
    return () => {
      listenerSet.delete(listenerCopy)
    }
  }, [listener])

  const enabled = useAuthenticated() === 'TRUE'

  useQuery({
    queryKey: [queryKey],
    throwOnError: false,
    enabled,
    queryFn: async ({ signal }) => {
      const events = eventClient.subscribeFixed(request, {
        signal,
        timeoutMs: 0,
      })
      for await (const event of events) {
        const eventPayload = event.event
        const eventCase = eventPayload.case
        if (eventCase && eventCase === 'ref') {
          const ref = eventPayload.value.ref
          listenerSet.forEach(cb => cb(ref))
        }
      }

      return {}
    },
  })
}

export function useFileSubscription(listener: (contentRef: Ref) => void) {
  useSubscription(fileSubscription, listener)
}
export function usePhotoSubscription(listener: (contentRef: Ref) => void) {
  useSubscription(photoSubscription, listener)
}
export function useCustomerSubscription(listener: (contentRef: Ref) => void) {
  useSubscription(customerSubscription, listener)
}
